import { zodResolver } from '@hookform/resolvers/zod';
import { UserCheck } from 'lucide-react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { TooltipLabel } from '@eluve/blocks';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  Textarea,
} from '@eluve/components';
import { useImpersonation, useUserIdFromSession } from '@eluve/session-helpers';

const impersonationFormSchema = z.object({
  reason: z
    .string()
    .min(1, 'Please provide a reason for impersonation')
    .max(500, 'Reason must be less than 500 characters'),
});

type ImpersonationFormData = z.infer<typeof impersonationFormSchema>;

interface ImpersonateUserButtonProps {
  userId: string;
  tenantId: string;
  userEmail: string;
  userName: string;
}

export const ImpersonateUserButton: React.FC<ImpersonateUserButtonProps> = ({
  userId,
  tenantId,
  userEmail,
  userName,
}) => {
  const { isEluveAdmin, isImpersonating } = useImpersonation();
  const currentUserId = useUserIdFromSession();
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();

  const form = useForm<ImpersonationFormData>({
    resolver: zodResolver(impersonationFormSchema),
    defaultValues: {
      reason: '',
    },
  });
  const disable = !isEluveAdmin || isImpersonating || userId === currentUserId;

  const handleSubmit = (data: ImpersonationFormData) => {
    const params = new URLSearchParams({
      targetUserId: userId,
      tenantId: tenantId,
      reason: data.reason,
      originUrl: window.location.href,
    });

    navigate(`/impersonate/callback?${params.toString()}`);
    setDialogOpen(false);
    form.reset();
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          disabled={disable}
          aria-label="Impersonate User"
        >
          <TooltipLabel label={`Impersonate ${userName || userEmail}`}>
            <UserCheck className="size-4" />
          </TooltipLabel>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Impersonate User</DialogTitle>
          <DialogDescription>
            You are about to impersonate{' '}
            <strong>{userName || userEmail}</strong>.
            <br />
            This will replace your current session with theirs until you
            explicitly exit impersonation.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reason for Impersonation</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="e.g., Troubleshooting user issue, Testing user permissions..."
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    This will be logged for audit purposes.
                  </FormDescription>
                </FormItem>
              )}
            />
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setDialogOpen(false);
                  form.reset();
                }}
              >
                Cancel
              </Button>
              <Button type="submit">Start Impersonation</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
